/**
 * Canonical category definitions and helpers.
 * Used by manual entry, voice input, and the receipt scanner to keep
 * category IDs and display labels consistent.
 */
import { INDUSTRY_PROFILES } from './industryProfiles';

const withDefaultIcon = (items) => items.map((item) => ({ icon: '', ...item }));

// Canonical category options shown in drop-downs
export const CATEGORY_OPTIONS = {
  income: withDefaultIcon([
    { id: 'facial', name: 'Facial Treatments' },
    { id: 'injectables', name: 'Injectables' },
    { id: 'body_treatments', name: 'Body Treatments' },
    { id: 'massage', name: 'Massage Services' },
    { id: 'hair', name: 'Hair Services' },
    { id: 'nails', name: 'Nail Services' },
    { id: 'brows', name: 'Brow Services' },
    { id: 'lashes', name: 'Lash Services' },
    { id: 'gift_card', name: 'Gift Card' },
    { id: 'makeup', name: 'Makeup Services' },
    { id: 'retail', name: 'Retail Products' },
    { id: 'packages', name: 'Beauty Packages' },
    { id: 'consulting', name: 'Beauty Consulting' },
    { id: 'training', name: 'Training Services' },
    { id: 'gov_funding', name: 'Gov Funding' },
    { id: 'loan', name: 'Loan' }
  ]),
  expenses: withDefaultIcon([
    { id: 'supplies', name: 'Supplies/Materials' },
    { id: 'general_expenses', name: 'General Expenses' },
    { id: 'retail_stock', name: 'Retail Products' },
    { id: 'utilities', name: 'Utilities' },
    { id: 'internet_telephone', name: 'Internet/Telephone' },
    { id: 'rent', name: 'Rent/Lease' },
    { id: 'staffCosts', name: 'Staff Costs' },
    { id: 'employer_prsi', name: 'Employer PRSI' },
    { id: 'employee_holiday_pay', name: 'Employee Holiday Pay' },
    { id: 'owner_pension', name: 'Owner Pension' },
    { id: 'naersa_employer_contributions', name: 'NAERSA Employer Contributions' },
    { id: 'naersa_employee_contributions', name: 'NAERSA Employee Contributions' },
    { id: 'insurance', name: 'Insurance' },
    { id: 'marketing', name: 'Marketing' },
    { id: 'travel_expenses', name: 'Travel' },
    { id: 'meals', name: 'Meals' },
    { id: 'tips', name: 'Tips' },
    { id: 'uniform_costs', name: 'Uniform Costs' },
    { id: 'equipment', name: 'Equipment' },
    { id: 'furniture', name: 'Furniture' },
    { id: 'electronics', name: 'Electronics' },
    { id: 'salon_tools', name: 'Salon Tools' },
    { id: 'vehicle', name: 'Vehicle Purchase' },
    { id: 'vehicle_fuel', name: 'Vehicle Fuel' },
    { id: 'vehicle_repairs_licensing', name: 'Vehicle Repairs & Licensing' },
    { id: 'accounting', name: 'Accounting' },
    { id: 'legal', name: 'Legal Services' },
    { id: 'interest', name: 'Interest' },
    { id: 'bank_charges', name: 'Bank Charges' },
    { id: 'loan_repayment', name: 'Loan Repayment' },
    { id: 'training', name: 'Courses & Learning' },
    { id: 'repairs', name: 'Repairs' },
    { id: 'cleaning', name: 'Cleaning' },
    { id: 'maintenance', name: 'Equipment Maintenance' },
    { id: 'software', name: 'Software/Apps' },
    { id: 'office', name: 'Office Supplies' },
    { id: 'medical_expenses', name: 'Medical Expenses' },
    { id: 'misc', name: 'Miscellaneous' }
  ])
};

// String constants retained for backwards compatibility (VAT tables, etc.)
export const CATEGORIES = {
  SUPPLIES: 'supplies',
  RETAIL_STOCK: 'retail_stock',
  MATERIALS: 'materials',
  SALON_TOOLS: 'salon_tools',
  BEAUTY_EQUIPMENT: 'beauty_equipment',

  HAIR_SERVICES: 'hairServices',
  NAIL_SERVICES: 'nailServices',
  SKIN_SERVICES: 'skinServices',
  INJECTABLES: 'injectables',
  BODY_TREATMENTS: 'body_treatments',
  BROW_SERVICES: 'browServices',
  LASH_SERVICES: 'lashServices',
  GIFT_CARD: 'gift_card',
  MAKEUP_SERVICES: 'makeupServices',
  MASSAGE_SERVICES: 'massageServices',
  GOV_FUNDING: 'gov_funding',
  LOAN: 'loan',
  DIRECTOR_LOAN: 'director_loan',

  OFFICE: 'office',
  UTILITIES: 'utilities',
  INTERNET_TELEPHONE: 'internet_telephone',
  RENT: 'rent',
  SOFTWARE: 'software',
  EQUIPMENT: 'equipment',
  LABOUR: 'staffCosts',
  EMPLOYER_PRSI: 'employer_prsi',
  EMPLOYEE_HOLIDAY_PAY: 'employee_holiday_pay',
  OWNER_PENSION: 'owner_pension',
  NAERSA_EMPLOYER_CONTRIBUTIONS: 'naersa_employer_contributions',
  NAERSA_EMPLOYEE_CONTRIBUTIONS: 'naersa_employee_contributions',
  INSURANCE: 'insurance',
  ACCOUNTING: 'accounting',
  LEGAL: 'legal',
  INTEREST: 'interest',
  BANK_CHARGES: 'bank_charges',
  TRAINING: 'training',
  MARKETING: 'marketing',
  ADVERTISING: 'advertising',
  GENERAL_EXPENSES: 'general_expenses',

  TRAVEL: 'travel_expenses', // canonical travel bucket (covers airfare/transport)
  VEHICLE_FUEL: 'vehicle_fuel',
  VEHICLE_REPAIRS_LICENSING: 'vehicle_repairs_licensing',
  MEALS: 'meals',
  ENTERTAINMENT: 'entertainment',
  TIPS: 'tips',
  LOAN_REPAYMENT: 'loan_repayment',

  MEDICAL_EXPENSES: 'medical_expenses',
  UNIFORM_COSTS: 'uniform_costs',
  CLEANING: 'cleaning',
  MAINTENANCE: 'maintenance',
  FURNITURE: 'furniture',
  ELECTRONICS: 'electronics',
  REPAIRS: 'repairs',
  VEHICLE: 'vehicle',
  TRAINING_MATERIALS: 'training_materials',
  MISC: 'misc',
  OTHER: 'other'
};

// Categories the OCR service may emit before normalization
export const RECEIPT_SCANNER_CATEGORIES = {
  TRAVEL: 'travel',
  HAIR_SERVICES: 'hairServices',
  NAIL_SERVICES: 'nailServices',
  SKIN_SERVICES: 'skinServices',
  MAKEUP_SERVICES: 'makeupServices',
  MASSAGE_SERVICES: 'massageServices',
  RENT: 'rent',
  UTILITIES: 'utilities',
  SUPPLIES: 'supplies',
  MARKETING: 'marketing',
  EQUIPMENT: 'equipment',
  TRAINING: 'training',
  SOFTWARE: 'software',
  OFFICE_SUPPLIES: 'office',
  RETAIL_STOCK: 'retail_stock',
  INTERNET_TELEPHONE: 'internet_telephone'
};

const INDUSTRY_CATEGORY_OPTIONS = Object.values(INDUSTRY_PROFILES).flatMap((profile) => [
  ...(profile.categories?.income || []),
  ...(profile.categories?.expenses || [])
]);

const CATEGORY_DISPLAY_LOOKUP = [
  ...CATEGORY_OPTIONS.income,
  ...CATEGORY_OPTIONS.expenses,
  ...INDUSTRY_CATEGORY_OPTIONS,
  { id: 'travel', name: 'Travel' },
  { id: 'transport', name: 'Travel' },
  { id: 'airfare', name: 'Travel' },
  { id: 'accommodation', name: 'Travel' },
  { id: 'director_loan', name: "Director's Loan" },
  { id: 'entertainment', name: 'Entertainment' },
  { id: 'other', name: 'Other Expenses' }
].reduce((acc, { id, name }) => {
  acc[id] = name;
  return acc;
}, {});

const CATEGORY_NORMALIZATION_MAP = {
  travel: CATEGORIES.TRAVEL,
  transport: CATEGORIES.TRAVEL,
  transportation: CATEGORIES.TRAVEL,
  airfare: CATEGORIES.TRAVEL,
  flight: CATEGORIES.TRAVEL,
  flights: CATEGORIES.TRAVEL,
  accommodation: CATEGORIES.TRAVEL,
  vehicle_fuel: CATEGORIES.VEHICLE_FUEL,
  vehiclefuel: CATEGORIES.VEHICLE_FUEL,
  'vehicle fuel': CATEGORIES.VEHICLE_FUEL,
  'car fuel': CATEGORIES.VEHICLE_FUEL,
  fuel: CATEGORIES.VEHICLE_FUEL,
  petrol: CATEGORIES.VEHICLE_FUEL,
  diesel: CATEGORIES.VEHICLE_FUEL,
  gasoline: CATEGORIES.VEHICLE_FUEL,
  unleaded: CATEGORIES.VEHICLE_FUEL,
  vehicle_repairs_licensing: CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  vehiclerepairslicensing: CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  'vehicle repairs licensing': CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  'vehicle repairs & licensing': CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  vehicle_repairs: CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  'vehicle repairs': CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  car_repairs: CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  'car repairs': CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  'car repair': CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  'vehicle repair': CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  'car service': CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  'vehicle service': CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  'auto service': CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  'auto repair': CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  vehicle_licensing: CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  'vehicle licensing': CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  motor_tax: CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  'motor tax': CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  road_tax: CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  'road tax': CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  car_tax: CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  'car tax': CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  vehicle_tax: CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  'vehicle tax': CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  nct: CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  mot: CATEGORIES.VEHICLE_REPAIRS_LICENSING,
  food: CATEGORIES.MEALS,
  meal: CATEGORIES.MEALS,
  meals: CATEGORIES.MEALS,
  food_and_drink: CATEGORIES.MEALS,
  'food and drink': CATEGORIES.MEALS,
  general_expense: CATEGORIES.GENERAL_EXPENSES,
  general_expenses: CATEGORIES.GENERAL_EXPENSES,
  'general expense': CATEGORIES.GENERAL_EXPENSES,
  'general expenses': CATEGORIES.GENERAL_EXPENSES,
  travel_expenses: CATEGORIES.TRAVEL,
  'travel expenses': CATEGORIES.TRAVEL,
  internet_telephone: CATEGORIES.INTERNET_TELEPHONE,
  internettelephone: CATEGORIES.INTERNET_TELEPHONE,
  'internet telephone': CATEGORIES.INTERNET_TELEPHONE,
  'internet/telephone': CATEGORIES.INTERNET_TELEPHONE,
  telecommunications: CATEGORIES.INTERNET_TELEPHONE,
  telecom: CATEGORIES.INTERNET_TELEPHONE,
  broadband: CATEGORIES.INTERNET_TELEPHONE,
  wifi: CATEGORIES.INTERNET_TELEPHONE,
  'wi-fi': CATEGORIES.INTERNET_TELEPHONE,
  telephone: CATEGORIES.INTERNET_TELEPHONE,
  phone_bill: CATEGORIES.INTERNET_TELEPHONE,
  phonebill: CATEGORIES.INTERNET_TELEPHONE,
  'phone bill': CATEGORIES.INTERNET_TELEPHONE,
  mobile_bill: CATEGORIES.INTERNET_TELEPHONE,
  'mobile bill': CATEGORIES.INTERNET_TELEPHONE,
  'mobile phone bill': CATEGORIES.INTERNET_TELEPHONE,
  tip: CATEGORIES.TIPS,
  tips: CATEGORIES.TIPS,
  gratuity: CATEGORIES.TIPS,
  gratuities: CATEGORIES.TIPS,
  loan_repayment: CATEGORIES.LOAN_REPAYMENT,
  loanrepayment: CATEGORIES.LOAN_REPAYMENT,
  'loan repayment': CATEGORIES.LOAN_REPAYMENT,
  'loan repayments': CATEGORIES.LOAN_REPAYMENT,
  'loan payment': CATEGORIES.LOAN_REPAYMENT,
  'loan payments': CATEGORIES.LOAN_REPAYMENT,
  repayment: CATEGORIES.LOAN_REPAYMENT,
  repayments: CATEGORIES.LOAN_REPAYMENT,
  gov_funding: CATEGORIES.GOV_FUNDING,
  govfunding: CATEGORIES.GOV_FUNDING,
  'gov funding': CATEGORIES.GOV_FUNDING,
  'government funding': CATEGORIES.GOV_FUNDING,
  'government grant': CATEGORIES.GOV_FUNDING,
  'government grants': CATEGORIES.GOV_FUNDING,
  grant: CATEGORIES.GOV_FUNDING,
  grants: CATEGORIES.GOV_FUNDING,
  loan: CATEGORIES.LOAN,
  'business loan': CATEGORIES.LOAN,
  'loan income': CATEGORIES.LOAN,
  'loan received': CATEGORIES.LOAN,
  director_loan: CATEGORIES.DIRECTOR_LOAN,
  directorloan: CATEGORIES.DIRECTOR_LOAN,
  "director's loan": CATEGORIES.DIRECTOR_LOAN,
  'directors loan': CATEGORIES.DIRECTOR_LOAN,
  'director loan': CATEGORIES.DIRECTOR_LOAN,
  'director loan account': CATEGORIES.DIRECTOR_LOAN,
  rct_contract_income: 'rct_contract_income',
  'rct contract income': 'rct_contract_income',
  rct_tax_income: 'rct_tax_income',
  'rct tax income': 'rct_tax_income',

  staff: CATEGORIES.LABOUR,
  staff_costs: CATEGORIES.LABOUR,
  staffcosts: CATEGORIES.LABOUR,
  'staff costs': CATEGORIES.LABOUR,
  payroll: CATEGORIES.LABOUR,
  prsi: CATEGORIES.EMPLOYER_PRSI,
  employer_prsi: CATEGORIES.EMPLOYER_PRSI,
  employerprsi: CATEGORIES.EMPLOYER_PRSI,
  'employer prsi': CATEGORIES.EMPLOYER_PRSI,
  "employer's prsi": CATEGORIES.EMPLOYER_PRSI,
  'ers prsi': CATEGORIES.EMPLOYER_PRSI,
  'employer paye prsi': CATEGORIES.EMPLOYER_PRSI,
  employee_holiday_pay: CATEGORIES.EMPLOYEE_HOLIDAY_PAY,
  employeeholidaypay: CATEGORIES.EMPLOYEE_HOLIDAY_PAY,
  holiday_pay: CATEGORIES.EMPLOYEE_HOLIDAY_PAY,
  holidaypay: CATEGORIES.EMPLOYEE_HOLIDAY_PAY,
  'holiday pay': CATEGORIES.EMPLOYEE_HOLIDAY_PAY,
  'employee holiday pay': CATEGORIES.EMPLOYEE_HOLIDAY_PAY,
  'staff holiday pay': CATEGORIES.EMPLOYEE_HOLIDAY_PAY,
  'holiday pay reserve': CATEGORIES.EMPLOYEE_HOLIDAY_PAY,
  'holiday reserve': CATEGORIES.EMPLOYEE_HOLIDAY_PAY,
  owner_pension: CATEGORIES.OWNER_PENSION,
  ownerpension: CATEGORIES.OWNER_PENSION,
  'owner pension': CATEGORIES.OWNER_PENSION,
  'owners pension': CATEGORIES.OWNER_PENSION,
  "owner's pension": CATEGORIES.OWNER_PENSION,
  naersa_employer_contributions: CATEGORIES.NAERSA_EMPLOYER_CONTRIBUTIONS,
  naersaemployercontributions: CATEGORIES.NAERSA_EMPLOYER_CONTRIBUTIONS,
  'naersa employer contributions': CATEGORIES.NAERSA_EMPLOYER_CONTRIBUTIONS,
  'naersa employer contribution': CATEGORIES.NAERSA_EMPLOYER_CONTRIBUTIONS,
  'naersa employer': CATEGORIES.NAERSA_EMPLOYER_CONTRIBUTIONS,
  naersa_employee_contributions: CATEGORIES.NAERSA_EMPLOYEE_CONTRIBUTIONS,
  naersaemployeecontributions: CATEGORIES.NAERSA_EMPLOYEE_CONTRIBUTIONS,
  'naersa employee contributions': CATEGORIES.NAERSA_EMPLOYEE_CONTRIBUTIONS,
  'naersa employee contribution': CATEGORIES.NAERSA_EMPLOYEE_CONTRIBUTIONS,
  'naersa employee': CATEGORIES.NAERSA_EMPLOYEE_CONTRIBUTIONS,
  salary: CATEGORIES.LABOUR,
  salaries: CATEGORIES.LABOUR,
  wage: CATEGORIES.LABOUR,
  wages: CATEGORIES.LABOUR,
  employee: CATEGORIES.LABOUR,
  contractor: CATEGORIES.LABOUR,
  labour: CATEGORIES.LABOUR,
  'labour costs': CATEGORIES.LABOUR,
  labor: CATEGORIES.LABOUR,
  'labor costs': CATEGORIES.LABOUR,
  subcontractor_rct: 'subcontractor_rct',
  'subcontractor rct': 'subcontractor_rct',
  'subcontractor (rct)': 'subcontractor_rct',
  rct_subcontractor: 'subcontractor_rct',
  'rct subcontractor': 'subcontractor_rct',
  outsourced_labour_non_rct: 'outsourced_labour_non_rct',
  'outsourced labour non rct': 'outsourced_labour_non_rct',
  'outsourced labour (non rct)': 'outsourced_labour_non_rct',
  outsourced_labor_non_rct: 'outsourced_labour_non_rct',
  'outsourced labor non rct': 'outsourced_labour_non_rct',
  rct_tax: 'rct_tax',
  'rct tax': 'rct_tax',
  consulting_fees: 'consulting_fees',
  'consulting fees': 'consulting_fees',
  'consultant fees': 'consulting_fees',
  management_consulting: 'management_consulting',
  'management consulting': 'management_consulting',
  business_advisory: 'business_advisory',
  'business advisory': 'business_advisory',
  project_management: 'project_management',
  'project management': 'project_management',
  medical_consulting: 'medical_consulting',
  'medical consulting': 'medical_consulting',
  retainer_income: 'retainer_income',
  retainer: 'retainer_income',
  'retainer income': 'retainer_income',
  workshops_training: 'workshops_training',
  'workshops training': 'workshops_training',
  professional_memberships: 'professional_memberships',
  'professional memberships': 'professional_memberships',
  professional_indemnity: 'professional_indemnity',
  'professional indemnity': 'professional_indemnity',
  research_reports: 'research_reports',
  'research reports': 'research_reports',
  'research & reports': 'research_reports',
  client_meetings: 'client_meetings',
  'client meetings': 'client_meetings',
  contractor_support: 'contractor_support',
  'contractor support': 'contractor_support',
  medical_equipment: 'medical_equipment',
  'medical equipment': 'medical_equipment',
  live_production: 'live_production',
  'live production': 'live_production',
  sound_engineering: 'sound_engineering',
  'sound engineering': 'sound_engineering',
  lighting_design: 'lighting_design',
  'lighting design': 'lighting_design',
  film_crew: 'film_crew',
  'film crew': 'film_crew',
  stage_crew: 'stage_crew',
  'stage crew': 'stage_crew',
  'roadie work': 'stage_crew',
  equipment_hire_income: 'equipment_hire_income',
  'equipment hire income': 'equipment_hire_income',
  production_management: 'production_management',
  'production management': 'production_management',
  crew_labour: 'crew_labour',
  'crew labour': 'crew_labour',
  crew_labor: 'crew_labour',
  'crew labor': 'crew_labour',
  audio_equipment: 'audio_equipment',
  'audio equipment': 'audio_equipment',
  lighting_equipment: 'lighting_equipment',
  'lighting equipment': 'lighting_equipment',
  camera_equipment: 'camera_equipment',
  'camera equipment': 'camera_equipment',
  production_consumables: 'production_consumables',
  'production consumables': 'production_consumables',
  venue_site_costs: 'venue_site_costs',
  'venue site costs': 'venue_site_costs',
  permits_licensing: 'permits_licensing',
  'permits licensing': 'permits_licensing',
  'permits & licensing': 'permits_licensing',
  freight_transport: 'freight_transport',
  'freight transport': 'freight_transport',

  office_supplies: CATEGORIES.OFFICE,
  officesupplies: CATEGORIES.OFFICE,
  'office supplies': CATEGORIES.OFFICE,
  office: CATEGORIES.OFFICE,
  accounting: CATEGORIES.ACCOUNTING,
  accountancy: CATEGORIES.ACCOUNTING,
  accountant: CATEGORIES.ACCOUNTING,
  accountants: CATEGORIES.ACCOUNTING,
  bookkeeping: CATEGORIES.ACCOUNTING,
  bookkeeper: CATEGORIES.ACCOUNTING,
  'accounting service': CATEGORIES.ACCOUNTING,
  'accounting services': CATEGORIES.ACCOUNTING,
  'accountancy service': CATEGORIES.ACCOUNTING,
  'accountancy services': CATEGORIES.ACCOUNTING,
  'bookkeeping service': CATEGORIES.ACCOUNTING,
  'bookkeeping services': CATEGORIES.ACCOUNTING,

  hairservices: CATEGORIES.HAIR_SERVICES,
  barber: 'hair',
  barbers: 'hair',
  barbering: 'hair',
  'barber services': 'hair',
  'barbering services': 'hair',
  nailservices: CATEGORIES.NAIL_SERVICES,
  skinservices: CATEGORIES.SKIN_SERVICES,
  injectable: CATEGORIES.INJECTABLES,
  injectables: CATEGORIES.INJECTABLES,
  bodytreatments: CATEGORIES.BODY_TREATMENTS,
  body_treatment: CATEGORIES.BODY_TREATMENTS,
  body_treatments: CATEGORIES.BODY_TREATMENTS,
  'body treatment': CATEGORIES.BODY_TREATMENTS,
  'body treatments': CATEGORIES.BODY_TREATMENTS,
  makeupservices: CATEGORIES.MAKEUP_SERVICES,
  massageservices: CATEGORIES.MASSAGE_SERVICES,
  browservices: CATEGORIES.BROW_SERVICES,
  lashservices: CATEGORIES.LASH_SERVICES,
  giftcard: CATEGORIES.GIFT_CARD,
  giftvoucher: CATEGORIES.GIFT_CARD,
  gift_voucher: CATEGORIES.GIFT_CARD,
  'gift card': CATEGORIES.GIFT_CARD,

  rentlease: CATEGORIES.RENT,
  'rent/lease': CATEGORIES.RENT,

  beauty: CATEGORIES.SUPPLIES,
  beauty_supplies: CATEGORIES.SUPPLIES,
  beauty_products: CATEGORIES.SUPPLIES,
  beauty_services: CATEGORIES.SUPPLIES,
  salon_supplies: CATEGORIES.SUPPLIES,
  suppliesmaterials: CATEGORIES.SUPPLIES,
  supplies_materials: CATEGORIES.SUPPLIES,

  tools_equipment: CATEGORIES.SALON_TOOLS,
  toolsequipment: CATEGORIES.SALON_TOOLS,
  beauty_equipment: CATEGORIES.EQUIPMENT,

  clothing: CATEGORIES.UNIFORM_COSTS,
  healthcare: CATEGORIES.MEDICAL_EXPENSES,
  medical: CATEGORIES.MEDICAL_EXPENSES,
  medical_expense: CATEGORIES.MEDICAL_EXPENSES,
  medical_expenses: CATEGORIES.MEDICAL_EXPENSES,

  interest: CATEGORIES.INTEREST,
  'interest expense': CATEGORIES.INTEREST,
  'interest paid': CATEGORIES.INTEREST,
  'bank interest': CATEGORIES.INTEREST,
  finance_interest: CATEGORIES.INTEREST,
  'finance interest': CATEGORIES.INTEREST,
  bank_charges: CATEGORIES.BANK_CHARGES,
  bankcharges: CATEGORIES.BANK_CHARGES,
  'bank charges': CATEGORIES.BANK_CHARGES,
  'bank charge': CATEGORIES.BANK_CHARGES,
  'bank fees': CATEGORIES.BANK_CHARGES,
  'bank fee': CATEGORIES.BANK_CHARGES,
  fees: CATEGORIES.BANK_CHARGES
};

const VALID_CATEGORY_IDS = new Set([
  ...CATEGORY_OPTIONS.income.map((c) => c.id),
  ...CATEGORY_OPTIONS.expenses.map((c) => c.id),
  ...INDUSTRY_CATEGORY_OPTIONS.map((c) => c.id)
]);

/**
 * Normalize a category string to the canonical category ID used by the app.
 */
export function normalizeCategoryId(categoryId) {
  if (!categoryId) return CATEGORIES.MISC;
  const trimmed = String(categoryId).trim();
  const lower = trimmed.toLowerCase();
  if (CATEGORY_NORMALIZATION_MAP[lower]) {
    return CATEGORY_NORMALIZATION_MAP[lower];
  }
  return trimmed;
}

/**
 * Maps Business Finance Tracker categories to receipt-scanner-friendly categories.
 */
export function mapToReceiptScannerCategory(beautyLedgerCategory) {
  const category = normalizeCategoryId(beautyLedgerCategory);
  const mapping = {
    [CATEGORIES.SUPPLIES]: RECEIPT_SCANNER_CATEGORIES.SUPPLIES,
    [CATEGORIES.RETAIL_STOCK]: RECEIPT_SCANNER_CATEGORIES.RETAIL_STOCK,
    [CATEGORIES.MATERIALS]: RECEIPT_SCANNER_CATEGORIES.SUPPLIES,
    [CATEGORIES.SALON_TOOLS]: RECEIPT_SCANNER_CATEGORIES.EQUIPMENT,
    [CATEGORIES.BEAUTY_EQUIPMENT]: RECEIPT_SCANNER_CATEGORIES.EQUIPMENT,

    [CATEGORIES.HAIR_SERVICES]: RECEIPT_SCANNER_CATEGORIES.HAIR_SERVICES,
    [CATEGORIES.NAIL_SERVICES]: RECEIPT_SCANNER_CATEGORIES.NAIL_SERVICES,
    [CATEGORIES.SKIN_SERVICES]: RECEIPT_SCANNER_CATEGORIES.SKIN_SERVICES,
    [CATEGORIES.MAKEUP_SERVICES]: RECEIPT_SCANNER_CATEGORIES.MAKEUP_SERVICES,
    [CATEGORIES.MASSAGE_SERVICES]: RECEIPT_SCANNER_CATEGORIES.MASSAGE_SERVICES,

    [CATEGORIES.OFFICE]: RECEIPT_SCANNER_CATEGORIES.OFFICE_SUPPLIES,
    [CATEGORIES.UTILITIES]: RECEIPT_SCANNER_CATEGORIES.UTILITIES,
    [CATEGORIES.INTERNET_TELEPHONE]: RECEIPT_SCANNER_CATEGORIES.INTERNET_TELEPHONE,
    [CATEGORIES.RENT]: RECEIPT_SCANNER_CATEGORIES.RENT,
    [CATEGORIES.SOFTWARE]: RECEIPT_SCANNER_CATEGORIES.SOFTWARE,
    [CATEGORIES.EQUIPMENT]: RECEIPT_SCANNER_CATEGORIES.EQUIPMENT,
    [CATEGORIES.TRAINING]: RECEIPT_SCANNER_CATEGORIES.TRAINING,
    [CATEGORIES.MARKETING]: RECEIPT_SCANNER_CATEGORIES.MARKETING,
    advertising: RECEIPT_SCANNER_CATEGORIES.MARKETING,

    [CATEGORIES.TRAVEL]: RECEIPT_SCANNER_CATEGORIES.TRAVEL,
    [CATEGORIES.VEHICLE_FUEL]: RECEIPT_SCANNER_CATEGORIES.TRAVEL,
    [CATEGORIES.MEALS]: CATEGORIES.MEALS
  };

  return mapping[category] || category;
}

/**
 * Map OCR/receipt scanner categories back to the app's canonical categories.
 */
export function mapFromReceiptScannerCategory(receiptScannerCategory) {
  const normalized = normalizeCategoryId(receiptScannerCategory);
  const mapping = {
    [RECEIPT_SCANNER_CATEGORIES.TRAVEL]: CATEGORIES.TRAVEL,
    travel: CATEGORIES.TRAVEL,
    transport: CATEGORIES.TRAVEL,
    airfare: CATEGORIES.TRAVEL,
    accommodation: CATEGORIES.TRAVEL,
    [CATEGORIES.VEHICLE_FUEL]: CATEGORIES.VEHICLE_FUEL,
    vehicle_fuel: CATEGORIES.VEHICLE_FUEL,
    fuel: CATEGORIES.VEHICLE_FUEL,
    petrol: CATEGORIES.VEHICLE_FUEL,
    diesel: CATEGORIES.VEHICLE_FUEL,
    gasoline: CATEGORIES.VEHICLE_FUEL,

    [RECEIPT_SCANNER_CATEGORIES.HAIR_SERVICES]: CATEGORIES.HAIR_SERVICES,
    [RECEIPT_SCANNER_CATEGORIES.NAIL_SERVICES]: CATEGORIES.NAIL_SERVICES,
    [RECEIPT_SCANNER_CATEGORIES.SKIN_SERVICES]: CATEGORIES.SKIN_SERVICES,
    [RECEIPT_SCANNER_CATEGORIES.MAKEUP_SERVICES]: CATEGORIES.MAKEUP_SERVICES,
    [RECEIPT_SCANNER_CATEGORIES.MASSAGE_SERVICES]: CATEGORIES.MASSAGE_SERVICES,
    [RECEIPT_SCANNER_CATEGORIES.RENT]: CATEGORIES.RENT,
    [RECEIPT_SCANNER_CATEGORIES.UTILITIES]: CATEGORIES.UTILITIES,
    [RECEIPT_SCANNER_CATEGORIES.SUPPLIES]: CATEGORIES.SUPPLIES,
    [RECEIPT_SCANNER_CATEGORIES.MARKETING]: CATEGORIES.MARKETING,
    [RECEIPT_SCANNER_CATEGORIES.EQUIPMENT]: CATEGORIES.EQUIPMENT,
    [RECEIPT_SCANNER_CATEGORIES.TRAINING]: CATEGORIES.TRAINING,
    [RECEIPT_SCANNER_CATEGORIES.SOFTWARE]: CATEGORIES.SOFTWARE,
    [RECEIPT_SCANNER_CATEGORIES.OFFICE_SUPPLIES]: CATEGORIES.OFFICE,
    [RECEIPT_SCANNER_CATEGORIES.RETAIL_STOCK]: CATEGORIES.RETAIL_STOCK,
    [RECEIPT_SCANNER_CATEGORIES.INTERNET_TELEPHONE]: CATEGORIES.INTERNET_TELEPHONE,
    telecommunications: CATEGORIES.INTERNET_TELEPHONE,
    telecom: CATEGORIES.INTERNET_TELEPHONE,
    broadband: CATEGORIES.INTERNET_TELEPHONE,
    telephone: CATEGORIES.INTERNET_TELEPHONE,
    phone_bill: CATEGORIES.INTERNET_TELEPHONE,

    beauty: CATEGORIES.SUPPLIES,
    beauty_supplies: CATEGORIES.SUPPLIES,
    beauty_products: CATEGORIES.SUPPLIES,
    beauty_services: CATEGORIES.SUPPLIES,
    salon_supplies: CATEGORIES.SUPPLIES,
    tools_equipment: CATEGORIES.SALON_TOOLS,
    beauty_equipment: CATEGORIES.EQUIPMENT,
    office_supplies: CATEGORIES.OFFICE,
    clothing: CATEGORIES.UNIFORM_COSTS,
    healthcare: CATEGORIES.MEDICAL_EXPENSES,
    medical: CATEGORIES.MEDICAL_EXPENSES
  };

  if (mapping[normalized]) {
    return mapping[normalized];
  }

  if (VALID_CATEGORY_IDS.has(normalized)) {
    return normalized;
  }

  // Preserve user-defined category ids (e.g. "cotton_wool") selected in UI.
  if (/^[a-z0-9][a-z0-9_-]{1,63}$/i.test(normalized)) {
    return normalized;
  }

  return CATEGORIES.MISC;
}

/**
 * Get a user-friendly display name for a category ID.
 */
export function getCategoryDisplayName(categoryId) {
  if (!categoryId) return 'Uncategorized';
  const normalized = normalizeCategoryId(categoryId);
  if (CATEGORY_DISPLAY_LOOKUP[normalized]) {
    return CATEGORY_DISPLAY_LOOKUP[normalized];
  }
  return normalized
    .replace(/[_-]/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
}

/**
 * Minimal icon helper for selects and badges.
 */
export function getCategoryIcon(categoryId) {
  const normalized = normalizeCategoryId(categoryId);
  const icons = {
    supplies: '',
    retail_stock: '',
    utilities: '',
    rent: '',
    internet_telephone: '',
    staffCosts: '',
    employer_prsi: '',
    employee_holiday_pay: '',
    owner_pension: '',
    naersa_employer_contributions: '',
    naersa_employee_contributions: '',
    insurance: '',
    marketing: '',
    travel_expenses: '',
    meals: '',
    tips: '',
    equipment: '',
    furniture: '',
    electronics: '',
    salon_tools: '',
    vehicle: '',
    vehicle_fuel: '',
    vehicle_repairs_licensing: '',
    accounting: '',
    legal: '',
    interest: '',
    bank_charges: '',
    director_loan: '',
    loan_repayment: '',
    training: '',
    repairs: '',
    cleaning: '',
    maintenance: '',
    software: '',
    office: '',
    general_expenses: '',
    gov_funding: '',
    loan: '',
    medical_expenses: '',
    misc: ''
  };
  return icons[normalized] || '';
}

export default CATEGORIES;
